/**
 * The HTTP half of qda's own AI client.
 *
 * qda talks to AI providers directly from the browser — it does NOT go through
 * the yaqatw plugin iframe (hostRpcClient.ts). The two are deliberately
 * independent: yaqatw keeps its provider keys in its own settings XML for the
 * coding/category agents, while everything here is configured from
 * Settings > AI and keeps working with the plugin absent or broken.
 *
 * The error-extraction, abort-tracking and JSON-repair behavior is ported from
 * yaqatw's service/ai/aibase.ts, which has been beaten into shape against
 * these same five providers' real responses.
 */

/** Every in-flight AI request, so a single kill-switch can cancel them all. */
const inFlight = new Set<AbortController>();

/** True for a fetch rejected because its AbortSignal fired — a cancel, not a failure. */
export function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

/** Aborts every outstanding AI request; returns how many there were. */
export function abortAllAi(): number {
  const n = inFlight.size;
  for (const controller of inFlight) controller.abort();
  inFlight.clear();
  return n;
}

export function aiInFlightCount(): number {
  return inFlight.size;
}

/**
 * POSTs JSON and returns the parsed response. Providers report failures in
 * three different shapes, hence the layered message extraction — surfacing the
 * provider's own wording ("invalid x-api-key", "model not found") is far more
 * useful in the menu than a bare status code.
 */
export async function aiFetch(
  url: string,
  init: { headers: Record<string, string>; body: unknown },
  signal?: AbortSignal
): Promise<any> {
  const controller = new AbortController();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  inFlight.add(controller);

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...init.headers },
      body: JSON.stringify(init.body),
      signal: controller.signal,
    });

    if (!res.ok) {
      let errorData: any = {};
      try {
        errorData = await res.json();
      } catch {
        errorData = {};
      }
      throw new Error(
        errorData?.error?.message ||
          errorData?.message ||
          errorData?.error ||
          `HTTP ${res.status}: ${res.statusText}`
      );
    }

    return await res.json();
  } finally {
    inFlight.delete(controller);
  }
}

/**
 * Strips the ```json fences models add despite being told not to, then repairs
 * the two malformations they reliably produce: trailing commas before a
 * closing brace/bracket, and raw control characters inside strings.
 */
export function cleanJsonResponse(raw: string): string {
  let text = (raw ?? "").trim();

  const fenced = text.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/);
  if (fenced) text = fenced[1].trim();

  // Some models prepend a sentence before the JSON. Fall back to the outermost
  // brace pair rather than failing the whole run over a stray preamble.
  if (text && text[0] !== "{" && text[0] !== "[") {
    const start = text.search(/[{[]/);
    const end = Math.max(text.lastIndexOf("}"), text.lastIndexOf("]"));
    if (start >= 0 && end > start) text = text.slice(start, end + 1);
  }

  return (
    text
      .replace(/,\s*([\]}])/g, "$1")
      // Control characters are exactly what we're here to strip — they are
      // illegal inside JSON strings and models do emit them. \n, \r and \t are
      // spared because they're valid escapes JSON.parse handles.
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "")
  );
}

/** Reads a dotted path ("choices.0.message.content") out of a parsed response. */
export function readPath(data: any, path: string): unknown {
  return path.split(".").reduce<any>((acc, key) => (acc == null ? acc : acc[key]), data);
}
